import React, { useState, useEffect, useRef } from 'react';
import TextInfo, {
  TEXT_FONT_WEIGHT,
  TEXT_COLOR,
  TEXT_SIZE
} from '@gc-npm/components.text';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { generateQueryString } from '@/utils/normalizers/product';
import Checkbox from '@/component/checkbox';
import { createBrowserHistory } from "history";
import styles from './style';
import { getFullUrl } from '@/utils/htmlUtils';
import getColor, { COLOR_DICTIONARY } from '@gc-npm/utils.enums.colors';

const EnumFilter = ({ filter, mobile, highlighted, disableCollapse = false, hideCount = false }) => {
  const [showMore, setShowMore] = useState(disableCollapse);
  const checkboxIdPrefix = `checkbox-${mobile ? 'mobile' : 'desktop'}-${filter.id}`;
  const {
    selectedFilters,
    sort,
    searcherQuery,
    productsFetching,
    id: idRoute,
  } = useSelector((state) => state.devicesList);
  const [filterSelected, setFilterSelected] = useState([]);
  const router = useRouter();
  const { query } = router;
  const history = typeof window === 'undefined' ? '' : createBrowserHistory();
  const debounceTimer = useRef(null);
  const filterSelectedRef = useRef([]);
  const userChangedFilter = useRef(false);

  const haveSameValues = (firstValue, secondValue) => (
    firstValue.length === secondValue.length &&
    firstValue.every((value) => secondValue.includes(value))
  );

  useEffect(() => {
    const name = filter.id;
    let nextFilterSelected = [];

    if (selectedFilters[name] !== undefined) {
      nextFilterSelected = selectedFilters[name];
    }

    if (!haveSameValues(nextFilterSelected, filterSelectedRef.current)) {
      filterSelectedRef.current = nextFilterSelected;
      setFilterSelected(nextFilterSelected);
    }

  }, [selectedFilters]);

  useEffect(() => () => {
    clearTimeout(debounceTimer.current);
  }, []);

  const applyFilterChange = (nextFilterSelected) => {
    const ModalOpen = history.location && history.location.hash === '#FiltersModal';
    let url;

    const name = filter.id;
    const selectedFiltersNew = { ...selectedFilters };
    //selectedFiltersNew.resultsFrom = 0;
    if (searcherQuery) selectedFiltersNew.query = searcherQuery;

    selectedFiltersNew[name] = nextFilterSelected;

    if (!productsFetching) {
      selectedFiltersNew.id = idRoute;
      url = getFullUrl(`/${idRoute}?sort=${sort}&${generateQueryString(
        selectedFiltersNew
      )}`, query);
      if (mobile)
        router.replace(`${url}${ModalOpen ? '#FiltersModal' : ''}`, undefined, { shallow: true });
      else
        router.push(`${url}${ModalOpen ? '#FiltersModal' : ''}`, undefined, { shallow: true });
      userChangedFilter.current = false;
    }
  };

  useEffect(() => {
    if (!userChangedFilter.current) return undefined;

    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      applyFilterChange(filterSelected);
    }, 500);

    return () => {
      clearTimeout(debounceTimer.current);
    };
  }, [filterSelected, selectedFilters, sort, searcherQuery, productsFetching, idRoute, query, mobile, router]);

  const setFilterChecked = (id, checked) => {
    const currentFilterSelected = filterSelectedRef.current;
    const nextFilterSelected = checked
      ? [...new Set([...currentFilterSelected, id])]
      : currentFilterSelected.filter((selectedId) => selectedId !== id);

    userChangedFilter.current = true;
    filterSelectedRef.current = nextFilterSelected;
    setFilterSelected(nextFilterSelected);
  };

  const toggleFilter = (id) => {
    const currentFilterSelected = filterSelectedRef.current;
    const nextFilterSelected = currentFilterSelected.includes(id)
      ? currentFilterSelected.filter((selectedId) => selectedId !== id)
      : [...currentFilterSelected, id];

    userChangedFilter.current = true;
    filterSelectedRef.current = nextFilterSelected;
    setFilterSelected(nextFilterSelected);
  };

  const handleShowMore = () => {
    setShowMore(!showMore);
  };
  const lastSelectedIndex = filter.values.reduce((acc, v, i) => filterSelected.includes(v.id) ? i : acc, -1);
  const visibleCount = Math.max(3, lastSelectedIndex + 1);
  const valuesToRender = showMore ? filter.values : filter.values.slice(0, visibleCount);

  return (
    <div className={`filterBox ${showMore ? 'more' : ''}`} style={highlighted ? { backgroundColor: getColor(COLOR_DICTIONARY.MOVISTAR_BLUE) } : undefined}>
      <div role='group'>
        {valuesToRender.map((option) => (
          <div
            key={option.id}
            className="filterContainer"
            onClick={() => toggleFilter(option.id)}
          >
            <TextInfo
              text={hideCount ? option.name : `${option.name} (${option.count})`}
              size={TEXT_SIZE.SUBTITLE2}
              color={highlighted ? TEXT_COLOR.WHITE : TEXT_COLOR.GREY_700}
              fontType={TEXT_FONT_WEIGHT.REGULAR}
            />
            <Checkbox
              defaultValue={filterSelected.includes(option.id)}
              id={`${checkboxIdPrefix}-${option.id}`}
              ariaLabel={hideCount ? option.name : `${option.name} (${option.count})`}
              onChange={(checked) => setFilterChecked(option.id, checked)}
              onClick={(event) => event.stopPropagation()}
              inverted={highlighted}
            />
          </div>
        ))}
      </div>
      {!showMore && filter.values.length > 3 && (
        <button type="button" onClick={handleShowMore} className="filterMore">
          <TextInfo
            text="Mostrar más >"
            size={TEXT_SIZE.CAPTION}
            color={highlighted ? TEXT_COLOR.WHITE : TEXT_COLOR.BLUEA}
            fontType={TEXT_FONT_WEIGHT.REGULAR}
          />
        </button>
      )}

      {showMore && !disableCollapse && filter.values.length > 3 && (
        <button type="button" onClick={handleShowMore} className="filterMore">
          <TextInfo
            text="Mostrar menos >"
            size={TEXT_SIZE.CAPTION}
            color={highlighted ? TEXT_COLOR.WHITE : TEXT_COLOR.BLUEA}
            fontType={TEXT_FONT_WEIGHT.REGULAR}
          />
        </button>
      )}
      <style jsx>{styles}</style>
    </div>
  );
};

export default EnumFilter;
